// One sign-in per browser, across every open tab of the tenant app.
//
// Without "Remember me" the session lives in sessionStorage, which belongs to ONE tab: a second tab
// opened on the app saw no session while the first stayed signed in, and signing out in one tab left
// the others showing protected pages. This keeps them together over BroadcastChannel("auth"):
//
//   SESSION_REQUEST / SESSION_SHARE  a tab that opens with no session asks the others for theirs;
//   LOGIN                            a sign-in or token refresh in any tab reaches every other tab;
//   LOGOUT / SESSION_EXPIRED         a session ending in any tab ends it in every tab.
//
// The end of a session is also written to localStorage (st.auth.lastEnd). That is the fallback for a
// browser without BroadcastChannel (the `storage` event carries it), and the record a tab that was
// asleep or reloaded checks against when it wakes — no polling anywhere.
//
// "Remember me" keeps its meaning: an un-remembered session still ends when the last tab holding it
// closes, because there is then nobody left to ask. BroadcastChannel is same-origin only.
//
// The Platform Console keeps its own sign-in in the same storage keys, so none of this runs there.

import { PLATFORM_BASE, PLATFORM_ONLY, SHOW_PLATFORM } from '@/config/appTarget';
import { storage, type SharedSession } from './storage';

/** The events every tab of this browser hears. */
export type SessionEndType = 'LOGOUT' | 'SESSION_EXPIRED';

type Message =
  | { type: 'SESSION_REQUEST'; id: string }
  | { type: 'SESSION_SHARE'; id: string; session: SharedSession }
  | { type: 'LOGIN'; session: SharedSession }
  | { type: SessionEndType };

const LAST_END = 'st.auth.lastEnd';

const channel: BroadcastChannel | null =
  typeof window !== 'undefined' && typeof BroadcastChannel !== 'undefined'
    ? new BroadcastChannel('auth')
    : null;

interface Handlers {
  /** The session ended in another tab; this tab's stored session is already cleared. */
  ended: (type: SessionEndType) => void;
  /** A session arrived from another tab — this tab was signed out, or it is a different user. */
  changed: () => void;
}

let handlers: Handlers | null = null;

/**
 * The Platform Console has its own sign-in; this sync belongs to the tenant app. The Platform build is
 * nothing but the console, the ERP build never is, and the combined app tells them apart by path.
 */
export function onPlatformConsole(): boolean {
  if (PLATFORM_ONLY) return true;
  if (!SHOW_PLATFORM) return false;
  return typeof window !== 'undefined' && window.location.pathname.startsWith(PLATFORM_BASE);
}

/** Who a token belongs to, read from its payload. Only ever compared, never trusted. */
function subjectOf(token: string | null | undefined): string | null {
  if (!token) return null;
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as Record<string, unknown>;
    const sub = json.sub ?? json.nameid ?? json.email;
    return typeof sub === 'string' ? sub : sub != null ? String(sub) : null;
  } catch {
    return null;
  }
}

function readLastEnd(raw?: string | null): { type: SessionEndType; at: number } | null {
  try {
    const value = raw ?? localStorage.getItem(LAST_END);
    if (!value) return null;
    const parsed = JSON.parse(value) as { type?: string; at?: number };
    if ((parsed.type === 'LOGOUT' || parsed.type === 'SESSION_EXPIRED') && typeof parsed.at === 'number')
      return { type: parsed.type, at: parsed.at };
  } catch { /* unreadable: treated as none */ }
  return null;
}

/**
 * Whether this browser ended the session this tab holds — while this tab was asleep, closed or
 * reloading and so missed the message. Compared by time: an end recorded after this tab's session
 * began ended it.
 */
export function sessionEndedElsewhere(): boolean {
  const since = storage.sessionSince;
  const end = readLastEnd();
  return since !== null && end !== null && end.at > since;
}

function handle(message: Message) {
  if (onPlatformConsole()) return;

  switch (message.type) {
    case 'SESSION_REQUEST': {
      const mine = storage.shared;
      if (mine && channel) channel.postMessage({ type: 'SESSION_SHARE', id: message.id, session: mine } satisfies Message);
      break;
    }
    case 'LOGIN': {
      const before = storage.accessToken;
      storage.adoptSession(message.session);
      // A refresh of the same user's session only updates the tokens. Anything else — this tab was
      // signed out, or another person signed in — means the profile on screen is no longer true.
      if (!before || subjectOf(before) !== subjectOf(message.session.accessToken)) handlers?.changed();
      break;
    }
    case 'LOGOUT':
    case 'SESSION_EXPIRED': {
      if (storage.accessToken || storage.refreshToken) storage.clearSession();
      handlers?.ended(message.type);
      break;
    }
    default:
      // SESSION_SHARE is answered to one waiting request, which listens for it itself.
      break;
  }
}

channel?.addEventListener('message', (event: MessageEvent<Message>) => {
  if (event.data && typeof event.data === 'object') handle(event.data);
});

// The fallback and the safety net: the `storage` event fires in every OTHER tab when a session end is
// recorded. With a channel the same end arrives there too; handling it twice is harmless.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event: StorageEvent) => {
    if (event.key !== LAST_END || !event.newValue) return;
    const end = readLastEnd(event.newValue);
    const since = storage.sessionSince;
    // No session left in storage (a remembered session is shared, so it went with the sign-out), or
    // one older than the sign-out: either way this tab is signed out now.
    if (end && (since === null || end.at > since)) handle({ type: end.type });
  });
}

// Every session this tab writes (sign-in, refresh) is handed to the others.
storage.onSessionWritten(() => {
  if (onPlatformConsole()) return;
  const session = storage.shared;
  if (channel && session) channel.postMessage({ type: 'LOGIN', session } satisfies Message);
});

/** Subscribes the session store to what other tabs do. Returns the unsubscribe. */
export function onSessionEvents(next: Handlers): () => void {
  handlers = next;
  return () => { if (handlers === next) handlers = null; };
}

/** Records and announces that this browser's session has ended — every tab signs out. */
export function publishSessionEnd(type: SessionEndType): void {
  if (onPlatformConsole()) return;
  try { localStorage.setItem(LAST_END, JSON.stringify({ type, at: Date.now() })); } catch { /* private mode */ }
  channel?.postMessage({ type } satisfies Message);
}

/**
 * Asks the other open tabs for their session and adopts the first one offered.
 *
 * <p>
 * Resolves true when a session was adopted (or this tab already had one), false when no tab
 * answered in time — then this tab really is signed out. A short wait: an open tab answers within
 * milliseconds, and nobody should stare at a spinner for a tab that is not there.
 * </p>
 */
export function requestSessionFromOtherTabs(timeoutMs = 300): Promise<boolean> {
  if (storage.shared) return Promise.resolve(true);
  const ch = channel;
  if (!ch || onPlatformConsole()) return Promise.resolve(false);

  const id = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

  return new Promise<boolean>((resolve) => {
    const finish = (adopted: boolean) => {
      ch.removeEventListener('message', listener);
      window.clearTimeout(timer);
      resolve(adopted);
    };
    const listener = (event: MessageEvent<Message>) => {
      const message = event.data;
      if (message?.type === 'SESSION_SHARE' && message.id === id) {
        storage.adoptSession(message.session);
        finish(true);
      }
    };
    ch.addEventListener('message', listener);
    const timer = window.setTimeout(() => finish(false), timeoutMs);
    ch.postMessage({ type: 'SESSION_REQUEST', id } satisfies Message);
  });
}
